import dayjs from "dayjs";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import logger from "./logger";
import { toast } from "react-toastify";

export const isMobile = () => {
  const userAgent = window?.navigator?.userAgent?.toLowerCase();
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  const isNarrowScreen = window?.innerWidth <= 768;

  return mobileRegex?.test(userAgent) || isNarrowScreen;
};

export const convertStringObjectToJson = (invalidJsonString) => {
  if (invalidJsonString && invalidJsonString?.length > 0) {
    const jsonString = invalidJsonString.replace(
      /([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g,
      '$1"$2"$3'
    );

    const validJsonString = jsonString?.replace(
      /:\s*([a-zA-Z0-9_]+)/g,
      ': "$1"'
    );

    return validJsonString ? JSON.parse(validJsonString) : {};
  }
  return {};
};

export const sortObjectByString = (a, b, property) => {
  const itemA = a?.[property] ? String(a?.[property]) : "";
  const itemB = b?.[property] ? String(b?.[property]) : "";
  return itemA.localeCompare(itemB);
};

export const sortObjectByNumber = (a, b, property) => {
  const itemA = a?.[property] || 0;
  const itemB = b?.[property] || 0;
  return itemA - itemB;
};

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return (
      <Component
        {...props}
        params={params}
        router={{ location, navigate, params }}
      />
    );
  }

  return ComponentWithRouterProp;
}

export const toDayjs = (value) => {
  if (dayjs.isDayjs(value)) return value;

  if (value == null) return null;

  try {
    return dayjs(value);
  } catch (error) {
    logger.warn("Failed to convert to dayjs object:", error);
    return null;
  }
};

export const getDayjsFromTimestamp = (timestamp) => {
  if (
    !timestamp ||
    (typeof timestamp !== "number" && typeof timestamp !== "string")
  ) {
    return null;
  }

  const isUnix = timestamp.toString().length === 10;
  const parsedDate = isUnix ? dayjs.unix(timestamp) : dayjs(timestamp);

  return parsedDate.isValid() ? parsedDate : null;
};

export const isToday = (timestamp) => {
  const isUnix = timestamp.toString().length === 10;
  const date = isUnix ? dayjs.unix(timestamp) : dayjs(timestamp);

  return date.isSame(dayjs(), "day");
};
export const getDateFromTimestamp = (timestamp, format = "MM-DD-YYYY") => {
  const date = getDayjsFromTimestamp(timestamp);
  return date ? date.format(format) : "-";
};

export const copyToClipboard = (text, message = "Text copied to clipboard") => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(message);
      },
      (err) => {
        toast.error("Failed to copy text: ", err);
      }
    );
  } else {
    toast.error("Clipboard API not supported");
  }
};

export const debounce = (func, wait) => {
  let timeoutId = null;

  return function (...args) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
};

export const convertPseudoJsonStringToObject = (string) => {
  try {
    const fixed = string
      .replace(/([{,]\s*)(\w+)\s*:/g, '$1"$2":')
      .replace(/:\s*([^,"}]+)/g, (match, val) => {
        if (!/^".*"$/.test(val.trim())) {
          return `: "${val.trim()}"`;
        }
        return match;
      });

    return JSON.parse(fixed);
  } catch (error) {
    return {};
  }
};

export const waitForMilliseconds = (ms) =>
  new Promise((resolve) => setTimeout(() => resolve(), ms));
